import React, { useState, useEffect } from "react";
import { Calendar, ClipboardList, FileCheck2, Timer, Settings, Users, FileText, FileClock } from "lucide-react";
import MyAttendance from "../../components/admin/tabs/Attendance/myAttendance";
import OvertimePolicy from "../../components/admin/tabs/Attendance/overtimePolicy";
import Requests from "../../components/admin/tabs/Attendance/attendanceRequests";
import Setting from "../../components/admin/tabs/Attendance/settingAttendance";
import ShiftTime from "../../components/admin/tabs/Attendance/shiftTime";
import AttendanceDetails from "../../components/admin/tabs/Attendance/attendanceDetails";
import RequestOvertime from "../../components/admin/tabs/Attendance/requestOvertime";
import MyOvertime from "../../components/admin/tabs/Attendance/myOvertime";
import { getEmployeeData } from "../../utils/localStorage";
import { getRequest } from "../../service";

const tabPermissionMap = {
  myAttendance: null, // special: only for non-companyadmin
  attendanceDetails: "Attendance Log",
  overtimePolicy: "Overtime Policy",
  overtimeRequest: "Overtime Request",
  attendanceRequest: "Attendance Request",
  shiftTime: "Shift Time",
  Setting: null 
};

const allTabs = [
  { id: "myAttendance", icon: <Calendar size={20} />, component: <MyAttendance /> },
  { id: "attendanceDetails", icon: <FileText size={20} />, component: <AttendanceDetails /> },
  // { id: "overtimePolicy", icon: <Timer size={20} />, component: <OvertimePolicy /> },
  { id: "overtimeRequest", icon: <FileClock size={20} />, component: <RequestOvertime /> },
  { id: "myOvertime", icon: <Timer size={20} />, component: <MyOvertime /> },
  { id: "attendanceRequest", icon: <ClipboardList size={20} />, component: <Requests /> },
  { id: "shiftTime", icon: <FileCheck2 size={20} />, component: <ShiftTime /> },
  // { id: "Setting", icon: <Settings size={20} />, component: <Setting /> },
];

const Attendance = () => {
  const [activeTab, setActiveTab] = useState("myAttendance");
  const [stats, setStats] = useState({
    todayPresent: 0,
    todayAbsent: 0,
    todayOnLeave: 0,
    totalEmployees: 0
  });
  const [loading, setLoading] = useState(false);
  const userData = getEmployeeData();
  const role = userData?.role;
  const attendancePerms = userData?.designation?.permissions?.Attendance || {};

  let filteredTabs = [];

  if (role === "companyadmin") {
    // Show all tabs except myAttendance and myOvertime (overtimeRequest should be shown for companyadmin)
    filteredTabs = allTabs.filter(
      tab => tab.id !== "myAttendance" && tab.id !== "myOvertime"
    );
  } else {
    filteredTabs = allTabs.filter(tab => {
      // MyOvertime: show only if not companyadmin
      if (tab.id === "myOvertime") {
        return true;
      }
      // RequestOvertime: show if user has permission
      if (tab.id === "overtimeRequest") {
        const permArr = attendancePerms["Overtime Request"];
        if (!Array.isArray(permArr) || permArr.length === 0) return false;
        return true;
      }
      if (tab.id === "myAttendance") {
        // Always show myAttendance for non-companyadmin
        return true;
      }
      if (tab.id === "attendanceRequest") {
        // Show if user has Attendance Request permission (same logic as overtimeRequest)
        const permArr = attendancePerms["Attendance Request"];
        if (!Array.isArray(permArr) || permArr.length === 0) return false;
        return true;
      }
      if (tab.id === "Setting") {
        // Show if any Attendance permission is not empty
        return Object.values(attendancePerms).some(arr => Array.isArray(arr) && arr.length > 0);
      }
      const permKey = tabPermissionMap[tab.id];
      if (!permKey) return true;
      const permArr = attendancePerms[permKey];
      // Hide if permission array is empty or not present
      return Array.isArray(permArr) && (permArr.includes("view") || permArr.length > 0);
    });
  }

  // If the activeTab is not in filteredTabs, reset to first available
  React.useEffect(() => {
    if (!filteredTabs.some(tab => tab.id === activeTab)) {
      setActiveTab(filteredTabs[0]?.id || "");
    }
    // eslint-disable-next-line
  }, [JSON.stringify(filteredTabs)]);

  // Fetch attendance stats
  const fetchAttendanceStats = async () => {
    if (role !== "companyadmin") return;
    
    setLoading(true);
    try {
      const companyId = userData?.userId;
      if (!companyId) return;
      
      const response = await getRequest(`/api/myattendance/stats?companyId=${companyId}`);
      if (response) {
        setStats({
          todayPresent: response.todayPresent || 0,
          todayAbsent: response.todayAbsent || 0,
          todayOnLeave: response.todayOnLeave || 0,
          totalEmployees: response.totalEmployees || 0
        });
      }
    } catch (error) {
      console.error("Error fetching attendance stats:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAttendanceStats();
  }, [role, userData?.userId]);

  const handleTabClick = (tab) => {
    setActiveTab(tab);
  };

  const activeComponent = filteredTabs.find(tab => tab.id === activeTab)?.component;
  const statsData = [
    {
      title: "Today Present",
      value: stats.todayPresent.toString(),
      icon: <Calendar className="h-6 w-6 text-green-500" />,
      color: "bg-green-50"
    },
    {
      title: "Today Absent",
      value: stats.todayAbsent.toString(),
      icon: <ClipboardList className="h-6 w-6 text-red-500" />,
      color: "bg-red-50"
    },
    {
      title: "Today On Leave",
      value: stats.todayOnLeave.toString(),
      icon: <FileCheck2 className="h-6 w-6 text-orange-500" />,
      color: "bg-orange-50"
    },
    {
      title: "Total Employee",
      value: stats.totalEmployees.toString(),
      icon: <Users className="h-6 w-6 text-blue-500" />,
      color: "bg-blue-50"
    }
  ];

  return (
    <div className="flex bg-white">
      <div className="w-64 border-r border-gray-200 bg-gray-50 fixed h-screen">
        <div className="p-4">
          <div className="flex flex-col gap-2">
            {filteredTabs.map((tab) => (
              <div key={tab.id}>
                <button
                  className={`text-left px-4 py-2 rounded-md !capitalize transition-colors duration-300 w-full flex items-center gap-2  cursor-pointer  ${
                    tab.id === activeTab
                      ? "bg-primary text-white"
                      : "hover:bg-gray-100 hover:text-primary"
                  }`}
                  onClick={() => handleTabClick(tab.id)}
                >
                  {tab.icon}
                  {tab.id.replace(/([A-Z])/g, ' $1').trim()}
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="flex-1 p-6 overflow-auto ml-64">
        {/* Stats Tiles */}
        {role === "companyadmin" && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {loading ? (
            // Loading skeleton
            Array.from({ length: 4 }).map((_, index) => (
              <div key={index} className="bg-gray-100 p-4 rounded-lg shadow-sm animate-pulse">
                <div className="h-4 bg-gray-200 rounded mb-2"></div>
                <div className="h-8 bg-gray-200 rounded"></div>
              </div>
            ))
          ) : (
            statsData.map((stat, index) => (
              <div key={index} className={`${stat.color} p-3 md:p-4 rounded-lg shadow-sm`}>
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-gray-600 text-sm">{stat.title}</p>
                    <h3 className="text-lg md:text-xl font-bold mt-1">{stat.value}</h3>
                  </div>
                  {stat.icon}
                </div>
              </div>
            ))
          )}
        </div>
        )}
        <div className="bg-white rounded-lg shadow-sm mt-4">
          {activeComponent}
        </div>
      </div>
    </div>
  );
};

export default Attendance;
